import { create } from 'zustand';
import { createDemoState, type DemoState } from '@/demo/demoData';

const STORAGE_KEY = 'ludolio.labs.demo.v1';

interface PersistedShape {
  isDemoMode: boolean;
  data: DemoState;
}

interface DemoStoreState {
  isDemoMode: boolean;
  data: DemoState | null;
  enterDemo: () => DemoState;
  exitDemo: () => void;
  updateData: (mutator: (draft: DemoState) => void) => void;
}

const readSession = (): PersistedShape | null => {
  if (typeof sessionStorage === 'undefined') return null;
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<PersistedShape>;
    if (!parsed?.isDemoMode || !parsed.data?.organization || !Array.isArray(parsed.data.projects)) return null;
    return { isDemoMode: true, data: parsed.data };
  } catch {
    return null;
  }
};

const writeSession = (value: PersistedShape | null) => {
  if (typeof sessionStorage === 'undefined') return;
  try {
    if (value) sessionStorage.setItem(STORAGE_KEY, JSON.stringify(value));
    else sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    return;
  }
};

const initial = readSession();

export const useDemoStore = create<DemoStoreState>((set, get) => ({
  isDemoMode: initial?.isDemoMode ?? false,
  data: initial?.data ?? null,
  enterDemo: () => {
    const fresh = createDemoState();
    set({ isDemoMode: true, data: fresh });
    writeSession({ isDemoMode: true, data: fresh });
    return fresh;
  },
  exitDemo: () => {
    set({ isDemoMode: false, data: null });
    writeSession(null);
  },
  updateData: (mutator) => {
    const current = get().data;
    if (!current) return;
    const next = structuredClone(current);
    mutator(next);
    set({ data: next });
    writeSession({ isDemoMode: true, data: next });
  },
}));
